/**
 * The editor's state (B6, TD §5 "Undo/redo", TD §8, TD §10): the plan document, its derived geometry and the
 * checks, plus selection, tool, view and the Unplaced tray.
 *
 * Every edit goes through `apply(op, args)` (one undo step, immer patches) or a drag (`beginDrag` →
 * `updateDrag` on every pointer move → `endDrag`, also one undo step). Undo keeps 200 steps. After each finished
 * edit, undo or redo the store calls `onCommit(doc)` (PlanFrame's commit → autosave); never mid-drag.
 * Checks are recalculated after each finished edit and at most a few times a second during a drag.
 */
import { applyPatches, enablePatches, produce, produceWithPatches, type Patch } from 'immer';
import { useSyncExternalStore } from 'react';
import * as api from '../api';
import { analysePlan } from '../../core/analyse';
import { derive, type Derived } from '../../core/geom';
import { newId } from '../../shared/plan-schema';
import type { Analysis, IconType, PlanDoc, PlanId, SpeciesId, SpeciesPick, SpeciesWithOverrides, UnplacedId } from '../../shared/types';

enablePatches();

/** Most undo steps kept (TD §5). */
export const UNDO_LIMIT = 200;
/** Checks run at most this often (ms) while dragging. */
const ANALYSE_THROTTLE_MS = 180;
/** Two `apply` calls with the same group within this time (ms) become one undo step. */
const GROUP_WINDOW_MS = 1500;

/** What is selected on the map. */
export type Selection =
  | { kind: 'area' | 'road' | 'river' | 'icon'; id: string }
  | { kind: 'plot' }
  | null;

/** Editor tools; the last four are Advanced only. */
export type ToolId = 'select' | 'road' | 'habitat' | 'lake' | 'river' | 'icon' | 'delete' | 'cut' | 'join' | 'boundary';

/** The view: zoom 1–8 and the map point (metres) at the centre of the canvas. */
export interface MapView {
  zoom: number;
  cx: number;
  cy: number;
}

export interface EditorState {
  doc: PlanDoc;
  derived: Derived;
  analysis: Analysis | null;
  species: SpeciesWithOverrides[] | null;
  speciesError: string | null;
  selection: Selection;
  tool: ToolId;
  /** Animal picked for the habitat tool. */
  toolSpecies: SpeciesId | null;
  /** Icon picked for the icon tool. */
  toolIcon: Exclude<IconType, 'entrance'>;
  view: MapView;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string;
  redoLabel: string;
  /** Habitats follow paths (FD §3.3, the prototype's toggle). */
  follow: boolean;
  advanced: boolean;
  showGrid: boolean;
  /** A short message for the map's status line, e.g. "Trimmed 2 habitats around the new path". */
  message: string;
  /** True while a drag is in progress. */
  dragging: boolean;
}

interface UndoEntry {
  label: string;
  patches: Patch[];
  inverse: Patch[];
  group?: string;
  at: number;
}

interface DragState {
  base: PlanDoc;
  label: string;
  recipe: ((d: PlanDoc) => void) | null;
}

/** Options for apply. */
export interface ApplyOptions {
  /** Undo label ("Move habitat"). */
  label?: string;
  /** Calls with the same group in quick succession are merged into one undo step (e.g. typing a width). */
  group?: string;
}

export interface EditorStoreOptions {
  planId: PlanId;
  doc: PlanDoc;
  /** Called with the new document after every finished edit, undo and redo. */
  onCommit(doc: PlanDoc): void;
  /** Species to use instead of loading them from the server (tests). */
  species?: SpeciesWithOverrides[];
}

const pref = (key: string, fallback: boolean): boolean => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
};
const setPref = (key: string, v: boolean) => {
  try { localStorage.setItem(key, v ? '1' : '0'); } catch { /* private mode */ }
};

// ---- The Unplaced tray (doc.unplaced, schema 2) --------------------------------------------------------

/** Puts an animal group into the plan's Unplaced tray (inside a draft); returns its id. */
export function addToTray(d: PlanDoc, speciesId: SpeciesId, count: number): UnplacedId {
  let id = newId();
  while (Object.hasOwn(d.unplaced, id)) id = newId();
  d.unplaced[id] = { speciesId, count: Math.min(500, Math.max(1, Math.round(count))) };
  return id;
}

/** The tray as this browser kept it before the tray moved into the plan (B6 first version). */
const oldTrayKey = (planId: string) => `zp.unplaced.${planId}`;

function readOldTray(planId: string): SpeciesPick[] {
  try {
    const raw = localStorage.getItem(oldTrayKey(planId));
    const list: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list.filter((x): x is SpeciesPick => !!x && typeof x.speciesId === 'string' && typeof x.count === 'number');
  } catch {
    return [];
  }
}

// ---- Species (shared by every editor) ----------------------------------------------------------------

let speciesPromise: Promise<SpeciesWithOverrides[]> | null = null;
/** Loads the species list once (built-ins with the shared corrections). `fresh` reloads it. */
export function loadSpecies(fresh = false): Promise<SpeciesWithOverrides[]> {
  if (!speciesPromise || fresh) {
    const p = api.listSpecies();
    speciesPromise = p;
    p.catch(() => { if (speciesPromise === p) speciesPromise = null; });
  }
  return speciesPromise;
}

// ---- The store ----------------------------------------------------------------------------------------

/** One open plan's editor state; see the file comment. */
export class EditorStore {
  readonly planId: PlanId;
  private state: EditorState;
  private listeners = new Set<() => void>();
  private undoStack: UndoEntry[] = [];
  private redoStack: UndoEntry[] = [];
  private drag: DragState | null = null;
  private analyseTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAnalysed = 0;
  private onCommit: (doc: PlanDoc) => void;
  private disposed = false;
  private messageTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: EditorStoreOptions) {
    this.planId = opts.planId;
    this.onCommit = opts.onCommit;
    const doc = opts.doc;
    this.state = {
      doc,
      derived: derive(doc),
      analysis: null,
      species: null,
      speciesError: null,
      selection: null,
      tool: 'select',
      toolSpecies: null,
      toolIcon: 'restaurant',
      view: { zoom: 1, cx: doc.size / 2, cy: doc.size / 2 },
      canUndo: false,
      canRedo: false,
      undoLabel: '',
      redoLabel: '',
      follow: pref('zp.follow', true),
      advanced: pref('zp.advanced', false),
      showGrid: pref('zp.grid', true),
      message: '',
      dragging: false,
    };
    if (opts.species) { this.setSpecies(opts.species); return; }
    loadSpecies().then(
      (sp) => this.setSpecies(sp),
      (e: unknown) => { if (!this.state.species) this.set({ speciesError: e instanceof Error ? e.message : 'The animal list could not be loaded.' }); },
    );
  }

  /** Current state (immutable snapshot; replaced on every change). */
  getState = (): EditorState => this.state;

  /** Subscribes to every change; returns the unsubscribe function. */
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  /** Stops timers. (React may keep using the store after a StrictMode re-run, so it keeps working.) */
  dispose(): void {
    this.disposed = true;
    if (this.analyseTimer) clearTimeout(this.analyseTimer);
    if (this.messageTimer) clearTimeout(this.messageTimer);
  }

  private set(patch: Partial<EditorState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  private stackFlags(): Pick<EditorState, 'canUndo' | 'canRedo' | 'undoLabel' | 'redoLabel'> {
    const u = this.undoStack[this.undoStack.length - 1], r = this.redoStack[this.redoStack.length - 1];
    return { canUndo: !!u, canRedo: !!r, undoLabel: u?.label ?? '', redoLabel: r?.label ?? '' };
  }

  /** Puts a new doc in place: derived geometry now, checks now or throttled, selection checked, commit if asked. */
  private setDoc(doc: PlanDoc, opts: { commit: boolean; extra?: Partial<EditorState> }): void {
    const derived = derive(doc);
    const selection = validSelection(doc, opts.extra?.selection !== undefined ? opts.extra.selection : this.state.selection);
    this.set({ ...opts.extra, doc, derived, selection, ...this.stackFlags() });
    if (opts.commit) {
      this.analyse(true);
      this.onCommit(doc);
    } else {
      this.analyse(false);
    }
  }

  /** Recalculates the checks: now, or at most every ANALYSE_THROTTLE_MS. */
  private analyse(now: boolean): void {
    if (!this.state.species) return;
    if (this.analyseTimer) { clearTimeout(this.analyseTimer); this.analyseTimer = null; }
    const run = () => {
      this.analyseTimer = null;
      this.lastAnalysed = performance.now();
      const { doc, derived, species } = this.state;
      if (!species) return;
      this.set({ analysis: analysePlan(doc, derived, species, doc.settings.extraSpacePct) });
    };
    const wait = ANALYSE_THROTTLE_MS - (performance.now() - this.lastAnalysed);
    if (now || wait <= 0) run();
    else this.analyseTimer = setTimeout(run, wait);
  }

  /** Replaces the species list (after loading or a shared correction) and re-checks. */
  setSpecies(species: SpeciesWithOverrides[]): void {
    this.set({ species, speciesError: null });
    this.analyse(true);
  }

  /** Reloads the species list from the server. */
  async refreshSpecies(): Promise<void> {
    this.setSpecies(await loadSpecies(true));
  }

  private pushUndo(entry: UndoEntry): void {
    const top = this.undoStack[this.undoStack.length - 1];
    if (entry.group && top && top.group === entry.group && entry.at - top.at < GROUP_WINDOW_MS && !this.redoStack.length) {
      top.patches = top.patches.concat(entry.patches);
      top.inverse = entry.inverse.concat(top.inverse);
      top.at = entry.at;
      return;
    }
    this.undoStack.push(entry);
    if (this.undoStack.length > UNDO_LIMIT) this.undoStack.splice(0, this.undoStack.length - UNDO_LIMIT);
    this.redoStack = [];
  }

  /**
   * Applies an op (or any draft mutator) as one undo step and commits it. Returns the op's result. A recipe that
   * changes nothing adds no step.
   */
  apply<A, R>(op: (draft: PlanDoc, args: A) => R, args?: A, opts: ApplyOptions = {}): R {
    if (this.drag) this.endDrag();
    let res!: R;
    const [next, patches, inverse] = produceWithPatches(this.state.doc, (d) => { res = op(d as PlanDoc, args as A); });
    if (!patches.length) return res;
    this.pushUndo({ label: opts.label ?? 'Change', patches, inverse, group: opts.group, at: Date.now() });
    this.setDoc(next, { commit: true });
    return res;
  }

  /**
   * Changes the doc without an undo step and without committing (for things PlanFrame already saved, such as
   * the plan's name after a rename).
   */
  quietly(recipe: (draft: PlanDoc) => void): void {
    if (this.drag) return;
    this.setDoc(produce(this.state.doc, (d) => { recipe(d as PlanDoc); }), { commit: false });
  }

  /** Starts a drag; nothing changes until updateDrag. */
  beginDrag(label: string): void {
    if (this.drag) this.endDrag();
    this.drag = { base: this.state.doc, label, recipe: null };
    this.set({ dragging: true });
  }

  /** The doc as it was when the drag started (for computing absolute positions). */
  dragBase(): PlanDoc | null {
    return this.drag?.base ?? null;
  }

  /** Shows the drag's current result: `recipe` is applied to the doc as it was at drag start. */
  updateDrag(recipe: (draft: PlanDoc) => void): void {
    const d = this.drag;
    if (!d) return;
    d.recipe = recipe;
    this.setDoc(produce(d.base, (draft) => { recipe(draft as PlanDoc); }), { commit: false });
  }

  /**
   * Ends the drag as one undo step. `after` runs in the same step on the final doc (e.g. trimming habitats off a
   * moved path). Returns false if the drag changed nothing.
   */
  endDrag(after?: (draft: PlanDoc) => void): boolean {
    const d = this.drag;
    this.drag = null;
    if (!d) return false;
    if (!d.recipe) { this.set({ dragging: false }); return false; }
    const recipe = d.recipe;
    const [next, patches, inverse] = produceWithPatches(d.base, (draft) => {
      recipe(draft as PlanDoc);
      if (after) after(draft as PlanDoc);
    });
    if (!patches.length) { this.setDoc(d.base, { commit: false, extra: { dragging: false } }); return false; }
    this.pushUndo({ label: d.label, patches, inverse, at: Date.now() });
    this.setDoc(next, { commit: true, extra: { dragging: false } });
    return true;
  }

  /** Abandons the drag and puts the doc back. */
  cancelDrag(): void {
    const d = this.drag;
    this.drag = null;
    if (d) this.setDoc(d.base, { commit: false, extra: { dragging: false } });
  }

  /** True while a drag is in progress. */
  isDragging(): boolean {
    return !!this.drag;
  }

  /** Undoes the last step. */
  undo(): void {
    if (this.drag) this.cancelDrag();
    const e = this.undoStack.pop();
    if (!e) return;
    this.redoStack.push(e);
    this.setDoc(applyPatches(this.state.doc, e.inverse), { commit: true, extra: {} });
    this.say(`Undid: ${e.label}`);
  }

  /** Redoes the last undone step. */
  redo(): void {
    if (this.drag) this.cancelDrag();
    const e = this.redoStack.pop();
    if (!e) return;
    this.undoStack.push(e);
    this.setDoc(applyPatches(this.state.doc, e.patches), { commit: true, extra: {} });
    this.say(`Redid: ${e.label}`);
  }

  /** Removes an animal group from the Unplaced tray ("Remove from the list"). One undo step. */
  removeFromTray(id: UnplacedId): void {
    this.apply((d: PlanDoc) => { delete d.unplaced[id]; }, undefined, { label: 'Remove from Unplaced' });
  }

  /**
   * One-time move of a tray this browser kept before the tray lived in the plan (`zp.unplaced.<planId>`):
   * its entries become one undoable edit and the old key is removed. Returns how many were moved.
   */
  adoptOldTray(): number {
    const old = readOldTray(this.planId);
    try { localStorage.removeItem(oldTrayKey(this.planId)); } catch { /* storage blocked */ }
    if (!old.length) return 0;
    this.apply((d: PlanDoc) => { for (const p of old) addToTray(d, p.speciesId, p.count); }, undefined, { label: 'Bring back Unplaced animals' });
    return old.length;
  }

  // ---- UI state (not undoable) ----

  select(selection: Selection): void {
    this.set({ selection: validSelection(this.state.doc, selection) });
  }
  setTool(tool: ToolId): void {
    if (this.drag) this.cancelDrag();
    this.set({ tool, ...(tool !== 'select' && this.state.selection?.kind === 'plot' ? { selection: null } : {}) });
  }
  setToolSpecies(id: SpeciesId | null): void {
    this.set({ toolSpecies: id });
  }
  setToolIcon(t: Exclude<IconType, 'entrance'>): void {
    this.set({ toolIcon: t });
  }
  setView(view: MapView): void {
    const z = Math.min(8, Math.max(1, view.zoom)), s = this.state.doc.size;
    this.set({ view: { zoom: z, cx: Math.min(s, Math.max(0, view.cx)), cy: Math.min(s, Math.max(0, view.cy)) } });
  }
  /** Fit: the whole map in view. */
  fit(): void {
    this.setView({ zoom: 1, cx: this.state.doc.size / 2, cy: this.state.doc.size / 2 });
  }
  setFollow(v: boolean): void { setPref('zp.follow', v); this.set({ follow: v }); }
  setShowGrid(v: boolean): void { setPref('zp.grid', v); this.set({ showGrid: v }); }
  setAdvanced(v: boolean): void {
    setPref('zp.advanced', v);
    const advTool = this.state.tool === 'cut' || this.state.tool === 'join' || this.state.tool === 'boundary';
    this.set({ advanced: v, ...(!v && advTool ? { tool: 'select' as ToolId } : {}), ...(!v && this.state.selection?.kind === 'plot' ? { selection: null } : {}) });
  }
  /** Shows a short message in the status line for a few seconds. */
  say(message: string): void {
    if (this.messageTimer) clearTimeout(this.messageTimer);
    this.messageTimer = message ? setTimeout(() => { this.messageTimer = null; this.set({ message: '' }); }, 7000) : null;
    this.set({ message });
  }
}

/** The selection if it still exists in the doc, else null. */
export function validSelection(doc: PlanDoc, s: Selection): Selection {
  if (!s) return null;
  if (s.kind === 'plot') return doc.plot ? s : null;
  const rec = s.kind === 'area' ? doc.areas : s.kind === 'road' ? doc.roads : s.kind === 'river' ? doc.rivers : doc.icons;
  return Object.hasOwn(rec, s.id) ? s : null;
}

// ---- Registry: the toolbar (rendered by PlanFrame) and the editor share one store per open plan -------

const registry = new Map<string, EditorStore>();
const registryListeners = new Set<() => void>();

/** Makes `store` the editor of its plan (or removes it with null). */
export function registerEditor(planId: string, store: EditorStore | null): void {
  if (store) registry.set(planId, store);
  else registry.delete(planId);
  for (const fn of registryListeners) fn();
}

/** The open editor of a plan, if any (React hook). */
export function useRegisteredEditor(planId: string): EditorStore | null {
  return useSyncExternalStore(
    (fn) => { registryListeners.add(fn); return () => registryListeners.delete(fn); },
    () => registry.get(planId) ?? null,
  );
}

const noStore = { subscribe: () => () => undefined, getState: () => null };

/** Selects a value from the editor state (React hook). The selector must return stable values. */
export function useEditor<T>(store: EditorStore, select: (s: EditorState) => T): T;
export function useEditor<T>(store: EditorStore | null, select: (s: EditorState) => T): T | null;
export function useEditor<T>(store: EditorStore | null, select: (s: EditorState) => T): T | null {
  const src = store ?? noStore;
  return useSyncExternalStore(src.subscribe, () => {
    const s = src.getState();
    return s ? select(s) : null;
  });
}
